import { Component, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import SyncSelect from 'react-select';
import AsyncSelect from 'react-select/async';
import capitalize from '~/utils/string';

export interface OptionProps {
  value: string | Record<string, unknown>;
  label: string;
}

interface SelectProps {
  value?: any;
  onChange: (...args: any[]) => void;
  onBlur?: (...args: any[]) => void;
  className?: string;
  placeholder?: string;
  labelClass?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  id?: string;
  name?: string;
  small?: boolean;
  isMulti?: boolean;
  defaultValue?:
    | Record<string, unknown>
    | Array<Record<string, unknown>>
    | null
    | undefined;
  options:
    | ((inputValue: string, callback: CallableFunction) => void)
    | Array<OptionProps>;
  components?: { [key: string]: Component<any> | FunctionComponent<any> };
}

const Select = ({
  options,
  value,
  onChange,
  onBlur,
  className,
  labelClass,
  placeholder,
  small,
  isDisabled = false,
  isSearchable,
  isMulti,
  isClearable,
  name,
  id,
  defaultValue,
  components = {},
}: SelectProps) => {
  const { t } = useTranslation();
  const isSync = Array.isArray(options);
  const SelectComponent = isSync ? SyncSelect : AsyncSelect;
  return (
    <SelectComponent
      defaultValue={defaultValue}
      isClearable={isClearable}
      name={name}
      id={id}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      components={{
        ...components,
        NoOptionsMessage: ({ innerProps }) => (
          <div {...innerProps} className="my-2 mx-3">
            {capitalize(t('geen opties'))}
          </div>
        ),
      }}
      styles={{
        menu: (provided) => ({
          ...provided,
          zIndex: 50,
        }),
        singleValue: (provided) => ({
          ...provided,
          fontSize: small && '0.75rem',
        }),
        option: (provided) => ({
          ...provided,
          fontSize: small && '0.75rem',
        }),
        placeholder: (provided) => ({
          ...provided,
          fontSize: small && '0.75rem',
        }),
        valueContainer: (provided) => ({
          ...provided,
          margin: small && 'auto 0',
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          height: small && '2rem',
        }),
        control: (provided) => ({
          ...provided,
          maxHeight: small && '2rem',
          minHeight: small && 0,
          outlineColor: 'hot-pink',
        }),
      }}
      onChange={onChange}
      onBlur={onBlur}
      classNamePrefix="c-select"
      className={`c-select ${className}`}
      labelClass={labelClass}
      placeholder={placeholder || capitalize(t('selecteer'))}
      value={value}
      isMulti={isMulti}
      options={isSync ? options : undefined}
      loadOptions={!isSync ? options : undefined}
    />
  );
};

export default Select;
